import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { EMPTY, Observable, catchError, first, map, of, switchMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient, private router: Router) {}

  public findPingFedUser(bearer: string): Observable<any> {
    let headers = this.setHeaders();
    headers = headers.set('Authorization', `Bearer ${bearer}`);
    headers = headers.set('DAPI-OauthServer', environment.gcp.oauthServer);

    const url: string = environment.gcp.identificationUrl;
    return this.http.post(url, {}, { headers, observe: 'response' }).pipe(
      map((res: HttpResponse<any>) => {
        const conversationId = res.headers.get('Dapi-Conversationid') || '';
        sessionStorage.setItem(this.getAuthTokenExpiryName(), this.getTimestampFromSeconds('3599'));
        sessionStorage.setItem(this.getAuthTokenName(), conversationId);
        return EMPTY;
      }),
      catchError((err) => {
        return EMPTY;
      })
    );
  }

  getCustomerInfo() {
    if (!sessionStorage.getItem('zeus-customerInfo')) {
      let headers = this.setHeaders(
        environment.gcp?.userInformation.dapiChannelId,
        environment.gcp?.userInformation.dapiClientSecret,
        environment.gcp?.userInformation.dapiAppId
      );
      const pingFedAccess = sessionStorage.getItem(this.getPingFedAccessToken());
      headers = headers.set('Authorization', `Bearer ${pingFedAccess}`);
      return this.http.get(environment.gcp.userInformation.url, { headers });
    } else {
      let headers = this.setHeaders(
        environment.gcp?.customerInfo.dapiChannelId,
        environment.gcp?.customerInfo.dapiClientSecret,
        environment.gcp?.customerInfo.dapiAppId
      );
      const pingFedAccess = sessionStorage.getItem(this.getPingFedAccessToken());
      headers = headers.set('Authorization', `Bearer ${pingFedAccess}`);
      return this.http.post(
        environment.gcp.customerInfo.url,
        { customerDetailAddOns: 'includeBasicDetails' },
        { headers }
      );
    }
  }

  order360Upgrade(firstName: string, surname: string, emailAddress: string) {
    return this.getCustomerInfo().pipe(
      switchMap((customer: any) => {

        let headers = this.setHeaders(
          environment.gcp?.dapiChannelId,
          environment.gcp?.dapiClientSecret,
          environment.gcp?.dapiAppId
        );
        
        const pingFedAccess = sessionStorage.getItem(this.getPingFedAccessToken());
        headers = headers.set('Authorization', `Bearer ${pingFedAccess}`);

        const siteId: string = customer?.GetUserInformationResponse?.success?.vmSiteID;
        const vmAccountId: string = customer?.GetUserInformationResponse?.success?.vmAccountID;
        let payload = {
          firstName,
          surname,
          emailAddress,
          accountNumber: Number(vmAccountId.substring(siteId.length)),
          siteId,
        };

        if (this.router.url.includes('/tv/upgrade-360-now')) {
          payload = {
            ...payload,
            ...{
              formName: 'TV360 GEN P2',
              businessProcessEmailAddress: 'AutomationCoekana@virginmedia.co.uk',
              emailSubject: 'automation tv upgrade',
            },
          };
        }

        return this.http.post(environment.gcp.order360UpgradeUrl, payload, { headers }).pipe(
          switchMap(() => {
            return of(null);
          })
        );
      })
    );
  }

  setHeaders(
    dapiChannelId = environment.gcp?.dapiChannelId,
    dapiClientSecret = environment.gcp?.dapiClientSecret,
    dapiAppId = environment.gcp?.dapiAppId
  ) {
    const uuid = uuidv4();
    let headers = new HttpHeaders();

    const conversationId = this.getConversationIdFromStorage();
    if (conversationId) {
      headers = headers.set('DAPI-ConversationId', conversationId);
    }
    headers = headers.set('DAPI-CorrelationID', uuid);
    headers = headers.set('DAPI-RequestID', uuid);
    headers = headers.set('DAPI-RequestTimestamp', new Date().toISOString());
    headers = headers.set('DAPI-ChannelID', dapiChannelId);
    headers = headers.set('DAPI-OriginatorID', uuid);
    headers = headers.set('Content-Type', 'application/json; charset=utf-8');
    headers = headers.set('DAPI-ClientSecret', dapiClientSecret);
    headers = headers.set('DAPI-AppID', dapiAppId);

    return headers;
  }

  public getConversationIdFromStorage(): any {
    const conversationId = sessionStorage.getItem(this.getAuthTokenName());
    const expiry = sessionStorage.getItem(this.getAuthTokenExpiryName());
    const now = new Date().getTime();
    if ((conversationId && expiry && parseInt(expiry, 10) > now) || (conversationId && expiry === 'NaN')) {
      return conversationId;
    }
  }

  public getTimestampFromSeconds(seconds): string {
    const expSec = seconds;
    const expDate = new Date();
    const expiryH = Math.floor(expSec / 3600);
    const expiryM = Math.floor((expSec % 3600) / 60);
    const expiryS = expSec % 60;
    expDate.setHours(expDate.getHours() + expiryH);
    expDate.setMinutes(expDate.getMinutes() + expiryM);
    expDate.setSeconds(expDate.getSeconds() + expiryS);
    return expDate.getTime().toString();
  }

  public getAuthTokenName() {
    return 'vmo2_sess_conversation_ID';
  }

  public getAuthTokenExpiryName() {
    return 'vmo2_sess_conversation_ID_expiry';
  }

  public getPingFedAccessToken() {
    return 'access_token';
  }

  getIdentityClaimProperty(name: string): string | null {
    const claims = this.getIdentityClaims();
    if (!claims) {
      return null;
    }
    /* eslint-disable @typescript-eslint/dot-notation */
    return claims[name];
  }

  getIdentityClaims() {
    const claims = sessionStorage.getItem('id_token_claims_obj');
    if (!claims) {
      return null;
    }
    return JSON.parse(claims);
  }
}
